import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Home from "./pages/Home.tsx";
import { AdminAuthProvider } from "./admin/AuthContext";
import ScrollToTop from "./components/ScrollToTop";

// Lazy-load all non-landing routes to reduce initial JS bundle size
const Index = lazy(() => import("./pages/Index.tsx"));
const ToolDetail = lazy(() => import("./pages/ToolDetail.tsx"));
const CoFound = lazy(() => import("./pages/CoFound.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const Blog = lazy(() => import("./pages/Blog.tsx"));
const BlogPost = lazy(() => import("./pages/BlogPost.tsx"));
const Privacy = lazy(() => import("./pages/legal/Privacy.tsx"));
const Terms = lazy(() => import("./pages/legal/Terms.tsx"));
const Affiliates = lazy(() => import("./pages/legal/Affiliates.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const Chapter1 = lazy(() => import("./pages/Chapter1.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const AdminLayout = lazy(() => import("./admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const Dashboard = lazy(() => import("./admin/pages/Dashboard"));
const ToolsManager = lazy(() => import("./admin/pages/ToolsManager"));
const BlogManagerPage = lazy(() => import("./admin/pages/BlogManager"));
const FundsManager = lazy(() => import("./admin/pages/FundsManager"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <AdminAuthProvider>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/tools" element={<Index />} />
              <Route path="/tools/:slug" element={<ToolDetail />} />
              <Route path="/co-found" element={<CoFound />} />
              <Route path="/about" element={<About />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/legal/privacy" element={<Privacy />} />
              <Route path="/legal/terms" element={<Terms />} />
              <Route path="/legal/affiliates" element={<Affiliates />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/chapter-1" element={<Chapter1 />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="tools" element={<ToolsManager />} />
                <Route path="blog" element={<BlogManagerPage />} />
                <Route path="funds" element={<FundsManager />} />
              </Route>
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AdminAuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
